import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';

import { merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { DeviceInformation } from '@rocksoft/mdm-sdk-v2';

import { AppleDeviceProfileResolverActions, selectProfileByType } from '@era/stores';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import {
  AsyncResultComponent,
  PaneComponent,
  PanelComponent,
  PanelFooterComponent,
  YesNoIconComponent,
} from '@rocksoft/cdk';
import { SecurityInfo } from '@rocksoft/mdm-sdk-v2';
import { isNotNullOrUndefined, isNotUndefined } from '@rocksoft/shared';
import { ToastService } from '@rocksoft/shared/toast';

import { OSSupportedPipe } from '../../pipes/os-supported.pipe';
import { AppleCommandProgressService } from '../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../utilities/command-spinner/command-spinner.component';
import { CommandProgress } from '../../viewmodels/apple/signalr/command-context.model';

@Component({
  selector: 'ml-mdm-apple-security',
  templateUrl: './apple-security.component.html',
  styleUrls: ['./apple-security.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PanelComponent,
    PaneComponent,
    AsyncResultComponent,
    YesNoIconComponent,
    PanelFooterComponent,
    CommandSpinnerComponent,
    FaIconComponent,
    AsyncPipe,
    OSSupportedPipe,
  ],
})
export class AppleSecurityComponent {
  private readonly store = inject(Store);
  private readonly mdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly udid = signal('');

  private readonly udid$: Observable<string> = toObservable(this.udid).pipe(
    filter(isNotNullOrUndefined),
    distinctUntilChanged()
  );

  readonly securityInfo$: Observable<SecurityInfo> = this.udid$.pipe(
    switchMap(() => this.store.select(selectProfileByType('security-info')).pipe(filter(isNotUndefined)))
  );

  readonly apple$: Observable<DeviceInformation> = this.udid$.pipe(
    switchMap(() => this.store.select(selectProfileByType('device-information')).pipe(filter(isNotUndefined)))
  );

  private _commandProgress = new Subject<CommandProgress>();
  readonly commandProgress$: Observable<CommandProgress> = this.udid$.pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'SecurityInfo'))
    )
  );

  constructor() {
    effect((onCleanup) => {
      const params$ = this.route.parent?.params;
      if (!params$) {
        this.udid.set('');
        return;
      }
      const sub = params$
        .pipe(
          map((p: Params) => p.udid),
          distinctUntilChanged()
        )
        .subscribe((_udid) => {
          this.udid.set(_udid);
        });
      onCleanup(() => {
        sub.unsubscribe();
      });
    });

    effect((onCleanup) => {
      const _udid = this.udid();
      if (!_udid) return;
      const sub = this.commandProgress$
        .pipe(
          filter((progress) => progress.status === 'Acknowledged'),
          distinctUntilChanged()
        )
        .subscribe((progress) => {
          this.store.dispatch(
            AppleDeviceProfileResolverActions.profileRequested({
              udid: progress.udid,
              profileType: 'security-info',
            })
          );
        });
      onCleanup(() => {
        sub.unsubscribe();
      });
    });
  }

  refresh() {
    const _udid = this.udid();
    if (!_udid) return;
    const command: CommandProgress = {
      udid: _udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'SecurityInfo',
    };
    this._commandProgress.next(command);
    this.mdmService.getSecurityInfo(_udid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
}
